<script lang="ts">
	import { slide } from 'svelte/transition'

	import fetchy from 'shared/fetchy'
	import fnum, { zero, type FinancialNumber } from 'shared/number'
	import { format_money_$_with_optional_cents } from 'shared/format_currency'
	import promise_in_flight_store from 'shared/promise_in_flight_store'
	import { storefront_prefix } from 'shared/var'
	const woocommerce_fetch = fetchy(storefront_prefix)

	import ErrorMessage from 'shared/component/ErrorMessage.svelte'
	import BigNumber from 'shared/component/BigNumber.svelte'
	import StorefrontNumberInput from 'shared/component/StorefrontNumberInput.svelte'

	export let configured_price: null | FinancialNumber
	export let product_title: string
	export let cart_endpoint: string
	export let shopping_cart_object: { [key: string]: any }
	export let disable_add_to_cart = false
	export let product_ref: string | number | null = null
	export let max_quantity: number = 999
	export let left_align = false

	let quantity = 1

	$: total = fnum(configured_price || zero).times(quantity.toString())

	let add_to_cart_promise: Promise<void>
	const add_to_cart = make_add_to_cart_endpoint()

	function make_add_to_cart_endpoint() {
		const nonce_promise = fetch(`/wp-admin/admin-ajax.php?action=rest-nonce`)
			.then(res => res.text())
			.then(nonce => nonce.trim()) // NOTE: trim() is important here, removing will break prod!

		return async function add_to_cart() {
			const nonce = await nonce_promise
			const cart_endpoint_with_nonce = nonce !== `0`
				? `/wp-json/cart${ cart_endpoint }?_wpnonce=${ nonce }`
				: `/wp-json/cart${ cart_endpoint }`

			add_to_cart_promise = woocommerce_fetch(cart_endpoint_with_nonce, {
				method: `POST`,
				body: {
					...shopping_cart_object,
					quantity,
				},
			})

			add_to_cart_promise.then(() => {
				// IF we have a product key send the `add_to_cart` event to GA here
				if (`gtag` in window && window.gtag && typeof window.gtag === `function` && product_ref) {
					window.gtag(`event`, `add_to_cart`, {
						event_category: `ecommerce`,
						event_label: `#${ product_ref }`,
						items: [{
							id: `#${ product_ref }`,
							name: product_title,
							quantity: `${ quantity }`,
						}],
					})
				}
			})
		}
	}

	$: request_in_flight = add_to_cart_promise && promise_in_flight_store(add_to_cart_promise)
</script>

{#if add_to_cart_promise}
	{#await add_to_cart_promise then _see_gh_issue_3704}
		<div class="success {left_align ? `left_align` : ``}" transition:slide|global>
			{product_title} added to cart. <a href="/cart">View Cart</a>
		</div>
	{:catch server_error_response}
		<ErrorMessage {server_error_response} />
	{/await}
{/if}

<div class="add_to_cart_container {left_align ? `left_align` : ``}">
	{#if total.gt(zero)}
		<output style="font-size: 28px; color: #999;">
			Total:
			<BigNumber>{ format_money_$_with_optional_cents(total) }</BigNumber>
		</output>
	{/if}

	{#if max_quantity > 1}
		<StorefrontNumberInput
			min={1}
			max={999}
			bind:value={quantity}
		/>
	{/if}

	<button
		on:click={add_to_cart}
		disabled={$request_in_flight || disable_add_to_cart}
	>
		Add to cart
	</button>
</div>

<style>
	.success {
		font-size: 16px;
		text-align: right;
	}
	.success.left_align {
		text-align: left;
	}

	.add_to_cart_container {
		display: flex;
		justify-content: flex-end;
		gap: 16px;
	}
	.add_to_cart_container.left_align {
		justify-content: flex-start;
	}

	.add_to_cart_container :global(input) {
		width: 48px;
		font-size: 14px;
	}
</style>
